import React, { useLayoutEffect, useState } from "react";
import { GetServerSideProps, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import PageLayoutV1 from "@app/hocs/PageLayoutV1";
import * as CmsHttp from "@app/http/cms";
import { META_DESCRIPTION, META_URL, REVALIDATE_SECONDS } from "@app/utilities/constants";
import SimpleCollapsibleHoc from "@components/hocs/SimpleCollapsibleHoc";
import { QnA } from "@models/Qna";

interface IFaqsList {
  ssrFaqs: QnA[];
}

export const FaqsList: React.FC<IFaqsList> = ({ ssrFaqs: faqs }) => {
  const [expandedCollapsibles, setExpandedCollapsibles] = useState<boolean[]>(faqs.map(() => false));
  const onCollapsibleToggled = (id: string, isExpanded: boolean) => {
    if (isExpanded) {
      const tmpExpandedCollapsibles = [...expandedCollapsibles],
        idx = parseInt(id);

      for (let i = 0, len = tmpExpandedCollapsibles.length; i < len; i++) {
        if (i !== idx && tmpExpandedCollapsibles[i] === true) {
          tmpExpandedCollapsibles[i] = false;
        } else if (i === idx) {
          tmpExpandedCollapsibles[i] = true;
        }
      }

      setExpandedCollapsibles(tmpExpandedCollapsibles);
    }
  };

  useLayoutEffect(() => {
    if (typeof window !== "undefined") {
      const faqSlugHash = (window.location.hash || "").slice(1);

      if (faqSlugHash) {
        const idx = faqs.findIndex((qna) => qna.slug === faqSlugHash);

        if (idx !== -1) {
          const tmpExpandedCollapsibles = [...expandedCollapsibles];
          tmpExpandedCollapsibles[idx] = true;
          setExpandedCollapsibles(tmpExpandedCollapsibles);
        }
      }
    }
  }, []);

  return (
    <div className="faqs-wrapper" itemScope itemType="https://schema.org/FAQPage">
      {faqs.length ? (
        faqs.map((qna, idx) => (
          <section key={idx} className="faq" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
            <meta itemProp="name" content={qna.question} />
            <SimpleCollapsibleHoc
              open={expandedCollapsibles[idx]}
              id={idx.toString()}
              title={qna.question}
              onToggled={onCollapsibleToggled}
              collapsibleProps={{
                itemScope: true,
                itemProp: "acceptedAnswer",
                itemType: "https://schema.org/Answer",
              }}
            >
              <div className="answer" itemProp="text" dangerouslySetInnerHTML={{ __html: qna.answer.html }}></div>
            </SimpleCollapsibleHoc>
          </section>
        ))
      ) : (
        <p className="text-center">No FAQs found</p>
      )}
    </div>
  );
};

const FaqsPage: NextPage<IFaqsList> = ({ ssrFaqs }: IFaqsList) => {
  const faqs = ssrFaqs as QnA[];

  /**
   * Meta contents
   */
  const metaTitle = `FAQ | Heritage Counseling`,
    metaDescription = META_DESCRIPTION || "",
    metaUrl = `${META_URL}/faqs`,
    metaImage = `${META_URL}/logo.png`;

  return (
    <PageLayoutV1 title="Frequently Asked Question">
      <Head>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:image" content={metaImage} />

        <meta name="twitter:url" content={metaUrl} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />

        <meta itemProp="title" content={metaTitle} />
        <meta itemProp="description" content={metaDescription} />

        <link rel="canonical" href={metaUrl} />
      </Head>
      <div className="faqs-page" data-testid="faqs-page">
        <FaqsList ssrFaqs={faqs} />
      </div>
    </PageLayoutV1>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const models = await CmsHttp.getFaqs();

  return {
    props: {
      ssrFaqs: models,
    } as IFaqsList,
    notFound: !models.length,
    revalidate: REVALIDATE_SECONDS, // In seconds
  };
};

export default FaqsPage;
